import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Eye, Paperclip, Pin, Upload, X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { errorMessage } from '@/api/errors'
import { useUploadFile } from '@/api/files'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { classesQuery } from '@/features/academic/api'
import { ToggleField } from '@/features/academic/components/ToggleField'
import { useAuth } from '@/features/auth/useAuth'
import { formatDateTime, formatNumber } from '@/utils/format'
import { useSaveAnnouncement } from '../api'
import { audienceLabel, categoryMeta, formatBytes, priorityMeta } from '../meta'
import {
  ANNOUNCEMENT_FIELDS,
  announcementSchema,
  toFormValues,
  toPayload,
  type AnnouncementFormValues,
} from '../schemas'
import {
  ANNOUNCEMENT_AUDIENCES,
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_PRIORITIES,
  type Announcement,
} from '../types'
import { AnnouncementBody } from './AnnouncementBody'
import { CategoryChip, CategoryTile } from './CategoryChip'
import { ClassChecklist } from './ClassChecklist'

const FORM_ID = 'announcement-form'

interface EditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  announcement: Announcement | null
}

/** Create / edit drawer with a live preview of how recipients will see the notice. */
export function AnnouncementEditor({ open, onOpenChange, announcement }: EditorProps) {
  const { t } = useTranslation('announcements')
  const save = useSaveAnnouncement()
  return (
    <Drawer
      open={open}
      onOpenChange={(o) => (save.isPending ? undefined : onOpenChange(o))}
      title={announcement ? t('Edit announcement') : t('New announcement')}
      description={t('Plain text only — line breaks are kept. Recipients see it in their Notices feed.')}
      className="max-w-5xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={save.isPending}>
            {announcement ? t('Save changes') : t('Create announcement')}
          </Button>
        </>
      }
    >
      <EditorForm
        announcement={announcement}
        onSubmit={async (payload) => {
          const saved = await save.mutateAsync({ ...payload, id: announcement?.id })
          toast.success(announcement ? t('Announcement updated') : t('Announcement created'), saved.title)
          onOpenChange(false)
        }}
      />
    </Drawer>
  )
}

function EditorForm({
  announcement,
  onSubmit,
}: {
  announcement: Announcement | null
  onSubmit: (p: ReturnType<typeof toPayload>) => Promise<void>
}) {
  const { t } = useTranslation('announcements')
  const { can, user } = useAuth()
  const withGuardians = useGuardians()
  const classesLabel = useId()
  const fileInput = useRef<HTMLInputElement>(null)
  const upload = useUploadFile()
  const classes = useQuery(classesQuery())
  const [attachment, setAttachment] = useState<{ name: string; size: number } | null>(
    announcement?.attachment
      ? { name: announcement.attachment.originalName, size: announcement.attachment.size }
      : null,
  )
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementSchema),
    defaultValues: toFormValues(announcement),
  })

  const values = watch()
  const submit = handleSubmit(async (v) => {
    try {
      await onSubmit(toPayload(v, announcement))
    } catch (e) {
      applyServerErrors(e, setError, ANNOUNCEMENT_FIELDS)
    }
  })

  const onFile = async (file: File | undefined) => {
    if (!file) return
    try {
      const stored = await upload.mutateAsync({ file, category: 'document' })
      setValue('attachmentFileId', stored.id, { shouldDirty: true })
      setAttachment({ name: stored.originalName, size: stored.size })
    } catch (e) {
      toast.error(t('Upload failed'), errorMessage(e))
    } finally {
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const classNames = (classes.data ?? []).filter((c) => values.classIds.includes(c.id)).map((c) => c.name)

  return (
    <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <form
        id={FORM_ID}
        onSubmit={(e) => void submit(e)}
        noValidate
        className="grid content-start gap-4 sm:grid-cols-2"
      >
        <FormField label={t('Title')} error={errors.title?.message} required className="sm:col-span-2">
          <Input
            maxLength={200}
            autoComplete="off"
            placeholder={t('e.g. School closed on Monday')}
            {...register('title')}
          />
        </FormField>
        <FormField
          label={t('Message')}
          error={errors.body?.message}
          required
          className="sm:col-span-2"
          hint={t('{{length}} / {{max}} characters · leave a blank line between paragraphs', {
            length: formatNumber(values.body.length),
            max: formatNumber(10_000),
          })}
        >
          <Textarea rows={8} {...register('body')} />
        </FormField>
        <FormField label={t('Category')} error={errors.category?.message} required>
          <Select
            options={ANNOUNCEMENT_CATEGORIES.map((c) => ({ value: c, label: t(categoryMeta[c].label) }))}
            {...register('category')}
          />
        </FormField>
        <FormField label={t('Priority')} error={errors.priority?.message}>
          <Select
            options={ANNOUNCEMENT_PRIORITIES.map((p) => ({ value: p, label: t(priorityMeta[p].label) }))}
            {...register('priority')}
          />
        </FormField>
        <FormField label={t('Audience')} error={errors.audience?.message} required className="sm:col-span-2">
          <Select
            options={ANNOUNCEMENT_AUDIENCES.filter((a) => withGuardians || a !== 'parents').map((a) => ({
              value: a,
              label: t(audienceLabel[a]),
            }))}
            {...register('audience')}
          />
        </FormField>
        {values.audience === 'class' ? (
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <span id={classesLabel} className="text-[0.8125rem] font-medium text-ink-soft">
              {t('Classes')}
              <span className="ml-0.5 text-danger-600" aria-hidden="true">
                *
              </span>
            </span>
            <Controller
              control={control}
              name="classIds"
              render={({ field }) => (
                <ClassChecklist
                  value={field.value}
                  onChange={field.onChange}
                  labelledBy={classesLabel}
                  invalid={Boolean(errors.classIds)}
                />
              )}
            />
            <p className="text-xs text-muted">
              {withGuardians
                ? t('Students of these classes and their guardians will see it.')
                : t('Students of these classes will see it.')}
            </p>
            {errors.classIds ? (
              <p className="text-xs font-medium text-danger-600" role="alert">
                {t(errors.classIds.message ?? errors.classIds.root?.message ?? 'Check the selected classes.')}
              </p>
            ) : null}
          </div>
        ) : null}

        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-[0.8125rem] font-medium text-ink-soft">{t('Publishing')}</legend>
          <Controller
            control={control}
            name="publishMode"
            render={({ field }) => (
              <RadioGroup
                appearance="cards"
                className="sm:grid-cols-3"
                value={field.value}
                onValueChange={field.onChange}
                aria-label={t('Publishing')}
                options={[
                  { value: 'draft', label: t('Save as draft'), description: t('Hidden until you publish.') },
                  {
                    value: 'now',
                    label: t('Publish now'),
                    description:
                      announcement?.status === 'published'
                        ? t('Keeps the current date.')
                        : t('Visible immediately.'),
                  },
                  { value: 'schedule', label: t('Schedule'), description: t('Goes live at a set time.') },
                ]}
              />
            )}
          />
        </fieldset>
        {values.publishMode === 'schedule' ? (
          <FormField label={t('Publish at')} error={errors.publishAt?.message} required>
            <Input type="datetime-local" {...register('publishAt')} />
          </FormField>
        ) : null}
        <FormField
          label={t('Expires at')}
          error={errors.expireAt?.message}
          hint={t('Optional. Hidden from feeds afterwards.')}
          className={values.publishMode === 'schedule' ? undefined : 'sm:col-span-2'}
        >
          <Input type="datetime-local" {...register('expireAt')} />
        </FormField>
        <Controller
          control={control}
          name="isPinned"
          render={({ field }) => (
            <ToggleField
              kind="switch"
              className="rounded-control border border-line bg-surface-muted/60 p-3 sm:col-span-2"
              label={t('Pin to the top')}
              description={t('Pinned notices stay above the rest of the feed.')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        {values.audience === 'all' ? (
          <Controller
            control={control}
            name="isPublic"
            render={({ field }) => (
              <ToggleField
                kind="switch"
                className="rounded-control border border-line bg-surface-muted/60 p-3 sm:col-span-2"
                label={t('Show on the public homepage')}
                description={t('Visitors of the school’s homepage see it in the news, without signing in.')}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        ) : null}

        {can('files.upload') ? (
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Attachment')}</p>
            <input
              ref={fileInput}
              type="file"
              className="sr-only"
              aria-label={t('Choose attachment')}
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            {attachment ? (
              <div className="flex items-center gap-3 rounded-control border border-line px-3 py-2.5">
                <Paperclip className="size-4 text-subtle" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{attachment.name}</span>
                <span className="text-xs text-muted">{formatBytes(attachment.size)}</span>
                <IconButton
                  label={t('Remove attachment')}
                  icon={<X />}
                  size="icon-sm"
                  onClick={() => {
                    setValue('attachmentFileId', null, { shouldDirty: true })
                    setAttachment(null)
                  }}
                />
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Upload />}
                loading={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {t('Attach a file')}
              </Button>
            )}
            {errors.attachmentFileId ? (
              <p className="mt-1 text-xs font-medium text-danger-600">
                {t(errors.attachmentFileId.message ?? '')}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-muted">
              {t('PDF, Word or image, up to 10 MB. Every recipient can download it.')}
            </p>
          </div>
        ) : null}
      </form>

      <aside aria-label={t('Preview')} className="lg:sticky lg:top-0 lg:self-start">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted uppercase">
          <Eye className="size-3.5" aria-hidden="true" /> {t('Live preview')}
        </p>
        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="flex items-start gap-3">
            <CategoryTile category={values.category} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <CategoryChip category={values.category} />
                {values.priority !== 'normal' ? (
                  <Badge tone={priorityMeta[values.priority].tone} size="sm">
                    {t(priorityMeta[values.priority].label)}
                  </Badge>
                ) : null}
                {values.isPinned ? (
                  <Badge tone="brand" size="sm">
                    <Pin aria-hidden="true" /> {t('Pinned')}
                  </Badge>
                ) : null}
              </div>
              <p className="mt-1.5 font-semibold break-words text-ink">
                {values.title.trim() || t('Your title')}
              </p>
              <p className="mt-1 text-xs text-subtle">
                {values.publishMode === 'schedule' && values.publishAt
                  ? t('From {{date}}', { date: formatDateTime(values.publishAt) })
                  : values.publishMode === 'draft'
                    ? t('Draft')
                    : t('Just now')}{' '}
                · {user?.name ?? t('You')}
              </p>
            </div>
          </div>
          <div className="mt-3 border-t border-line pt-3">
            {values.body.trim() ? (
              <AnnouncementBody text={values.body} className="break-words" />
            ) : (
              <p className="text-sm text-subtle">{t('Your message will appear here.')}</p>
            )}
            {attachment ? (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-control bg-surface-sunken px-2.5 py-1.5 text-xs text-ink-soft">
                <Paperclip className="size-3.5" aria-hidden="true" /> {attachment.name}
              </p>
            ) : null}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted">
          {t('Sent to')}{' '}
          <span className="font-medium text-ink-soft">
            {values.audience === 'class'
              ? classNames.join(', ') || t('no class selected yet')
              : t(audienceLabel[values.audience])}
          </span>
          {values.expireAt ? ` · ${t('until {{date}}', { date: formatDateTime(values.expireAt) })}` : ''}
        </p>
      </aside>
    </div>
  )
}
